import {
	Button,
	FlexRow,
	Icon,
	Input,
	InputContainer,
	Label,
	NoAutoFillInput,
	RequiredMarker,
	Tooltip,
} from "@components";
import { frameContractUUIDSchema } from "@models/AssetDivision";
import { NewDivision } from "@models/Division";
import { colors } from "@theme/theming";
import React from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import z from "zod";
import { Row } from "./StyledComponents";

const DEMO_FRAME_CONTRACT_UUID = "00000000-0000-4000-8000-000000000000";
const INTEGRATION_FRAME_CONTRACT_UUID = "11111111-1111-4111-8111-111111111111";

function JobRadSettings() {
	const { register, formState, setValue } = useFormContext<NewDivision>();

	const populateIdButtons = [
		{
			label: "Demo Rahmenvertrag ID",
			value: DEMO_FRAME_CONTRACT_UUID,
		},
		{
			label: "Integration Rahmenvertrag ID",
			value: INTEGRATION_FRAME_CONTRACT_UUID,
		},
	];

	return (
		<Container>
			<InputContainer>
				<Label htmlFor="frameContractUUID" style={{ width: "100%" }}>
					<Row style={{ justifyContent: "space-between" }}>
						<div
							style={{
								display: "flex",
								columnGap: "0.4rem",
								alignItems: "center",
							}}
						>
							<span>
								Rahmenvertrags ID
								<RequiredMarker
									message={
										formState.errors?.providerSettings?.jobrad
											?.frameContractUUID?.message
									}
								/>
							</span>
							<Tooltip
								id="frameContractUUIDTooltip"
								tooltip="Bei Trial-Standorten können nur die Demo-Rahmenvertrags-ID (kein Abruf von Events) oder die Integrations-Rahmenvertrags-ID (Verbindung zu JobRad LOFINO Dev) verwendet werden; dafür den Button rechts verwenden."
							>
								<Icon.Info color={colors.primary.p200} />
							</Tooltip>
						</div>
						<Row>
							{populateIdButtons.map((button) => (
								<PopulateIDButton
									onClick={() =>
										setValue(
											"providerSettings.jobrad.frameContractUUID",
											button.value,
											{ shouldDirty: true, shouldValidate: true }
										)
									}
									variant="outline-primary"
									size="sm"
								>
									{button.label}
								</PopulateIDButton>
							))}
						</Row>
					</Row>
				</Label>
				<Input
					id={"providerSettings.jobrad.frameContractUUID"}
					{...register("providerSettings.jobrad.frameContractUUID", {
						required: "ID ist erforderlich",
						validate: (value) =>
							frameContractUUIDSchema.safeParse(value.toString().trim())
								.success || "Die ID sollte UUID sein",
					})}
				/>
			</InputContainer>
			<InputContainer>
				<CompanyList>
					<NewCompanyForm>
						<InputContainer>
							<Label>
								Unternehmens ID{" "}
								<RequiredMarker
									message={
										formState.errors?.providerSettings?.jobrad?.companyId
											?.message
									}
								/>
							</Label>
							<Input
								id={"providerSettings.jobrad.companyId"}
								{...register("providerSettings.jobrad.companyId", {
									required: "ID ist erforderlich",
									validate: (value) =>
										z.uuid().safeParse(value.toString().trim()).success ||
										"Die ID sollte UUID sein",
								})}
							/>
						</InputContainer>
						<InputContainer>
							<Label>
								Unternehmens URL{" "}
								<RequiredMarker
									message={
										formState.errors?.providerSettings?.jobrad?.companyUrl
											?.message
									}
								/>
							</Label>
							<Input
								id={"providerSettings.jobrad.companyUrl"}
								as={NoAutoFillInput}
								{...register("providerSettings.jobrad.companyUrl", {
									required: "URL ist erforderlich",
									validate: (value) =>
										z.url().safeParse(value.toString().trim()).success ||
										"Die URL ist ungültig",
								})}
							/>
						</InputContainer>
					</NewCompanyForm>
				</CompanyList>
			</InputContainer>
		</Container>
	);
}

const Container = styled.div`
	background: ${colors.gray.g100};
	border-radius: 5px;
	padding: 1rem;
	display: flex;
	flex-direction: column;
	row-gap: 1rem;
`;

const CompanyList = styled.div`
	display: flex;
	flex-direction: column;
	row-gap: 0.7rem;
`;

const NewCompanyForm = styled(FlexRow)`
	column-gap: 1rem;
	align-items: flex-end;
`;

const PopulateIDButton = styled(Button)`
	padding: 0.25rem !important;
	font-size: 0.77rem !important;
	text-transform: none;
	font-weight: normal;
	&.btn-outline-primary {
		background-color: transparent !important;
		&:hover {
			box-shadow: none !important;
		}
	}
`;

export default JobRadSettings;
